/**
 * The eight semantic state names FE-1 Stage Work Item §4.1 E requires this system
 * to represent, each mapped to the axis that actually owns it.
 *
 * WHY THIS TABLE EXISTS
 *   The eight names are a PRESENTATION vocabulary. They are not eight members of
 *   one enum, and pretending they were is precisely how the frozen separations
 *   collapse. Two of them are the same word on two different axes (`Unknown` is
 *   both a presence and a freshness state), three of them are not contract states
 *   at all, and one of them is not a state but a property carried by every
 *   observation.
 *
 *   This table is the reviewable bridge between the mandated vocabulary and the
 *   typed axes, so a reviewer can check the mapping instead of inferring it.
 */

export type SemanticStateName =
  'Loading' | 'Unknown' | 'Empty' | 'Error' | 'Stale' | 'Degraded' | 'Unavailable' | 'NonAuthority';

/** Which axis owns a name. A name may be owned by more than one axis. */
export type SemanticRole = 'PRESENCE' | 'FRESHNESS' | 'AUTHORITY' | 'FRONTEND_CONDITION';

export interface SemanticVocabularyEntry {
  /** The mandated name. */
  readonly name: SemanticStateName;
  /** Every axis that owns this name. More than one entry means the name is ambiguous. */
  readonly roles: readonly SemanticRole[];
  /** True only when a frozen contract defines the state. */
  readonly contractOwned: boolean;
  /** What the state means. */
  readonly definition: string;
  /** How to tell it apart from the state it is most often confused with. */
  readonly disambiguation: string;
}

/**
 * The eight names, exhaustively. A missing key is a compile error, so this table
 * cannot silently fall behind the mandated vocabulary.
 */
export const SEMANTIC_VOCABULARY = Object.freeze({
  Loading: {
    name: 'Loading',
    roles: ['FRONTEND_CONDITION'],
    contractOwned: false,
    definition: 'No contract outcome has been obtained yet.',
    disambiguation:
      'NOT a statement about the subject. Carries no presence and no freshness, so it cannot be read as UNKNOWN (FE-1 Stage Work Item §7.5).',
  },
  Unknown: {
    name: 'Unknown',
    roles: ['PRESENCE', 'FRESHNESS'],
    contractOwned: true,
    definition:
      'On the presence axis: the read plane resolved and the subject data is absent — a normal answer, not an error. On the freshness axis: an answer exists but how current it is cannot be established — a caveat on an answer.',
    disambiguation:
      'The two UNKNOWNs are different states that share a spelling. They are branded differently and are not assignable to each other; collapsing them is a compile error.',
  },
  Empty: {
    name: 'Empty',
    roles: ['FRONTEND_CONDITION'],
    contractOwned: false,
    definition: 'A resolved projection contained zero items.',
    disambiguation:
      'NOT a substitute for UNKNOWN. On the Capability Definition read plane an empty SOURCE maps to UNKNOWN, never to empty (FE-1 Stage Work Item §6.5), so this condition must not be produced from that plane at all.',
  },
  Error: {
    name: 'Error',
    roles: ['FRONTEND_CONDITION'],
    contractOwned: false,
    definition:
      'A frontend engineering failure: a render fault, a wiring fault, a contract violation caught in code.',
    disambiguation:
      'NOT a contract outcome. The frozen contract has no error status; a reader failure is reported by the contract as UNAVAILABLE. ERROR must never absorb UNKNOWN and never render as a data absence.',
  },
  Stale: {
    name: 'Stale',
    roles: ['FRESHNESS'],
    contractOwned: true,
    definition: 'The answer is aged.',
    disambiguation:
      'NOT an error, and NOT permission to proceed: the frozen enum states "stale inputs do not become ALLOW".',
  },
  Degraded: {
    name: 'Degraded',
    roles: ['FRESHNESS'],
    contractOwned: true,
    definition: 'The answer is current but comes from a diminished source.',
    disambiguation: 'NOT an absence, and NOT a failure of the read.',
  },
  Unavailable: {
    name: 'Unavailable',
    roles: ['PRESENCE'],
    contractOwned: true,
    definition: 'A functioning reader could not supply a trustworthy response.',
    disambiguation:
      'NOT success, and NOT an empty or unknown answer. Carries no data and no diagnostic (FE-1 Stage Work Item §6.4).',
  },
  NonAuthority: {
    name: 'NonAuthority',
    roles: ['AUTHORITY'],
    contractOwned: true,
    definition:
      'The fixed property carried by every read-model observation: a read result is never permission to act.',
    disambiguation:
      'NOT a state an observation can be in instead of another state — it is carried ALONGSIDE every state. Structurally non-elevatable; there is no second authority value and no elevation function.',
  },
} satisfies Readonly<Record<SemanticStateName, SemanticVocabularyEntry>>);

/** The eight names as a tuple, for exhaustive iteration. */
export const SEMANTIC_STATE_NAMES = Object.freeze([
  'Loading',
  'Unknown',
  'Empty',
  'Error',
  'Stale',
  'Degraded',
  'Unavailable',
  'NonAuthority',
] as const satisfies readonly SemanticStateName[]);
